import { Component, computed, inject, input, OnChanges, OnInit, signal, SimpleChanges } from "@angular/core";
import { FaIconLibrary, FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faAngleDown, faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { TranslatePipe } from "@ngx-translate/core";

import { PercentageValuePipe } from "../../../../../core/pipes";
import { AdaaHelper } from "../../../../../core/utils";
import { Constants } from "../../../../constants/constants";
import { AdaaBoolean, Language } from "../../../../constants/enums";
import { ItemModel, StructureViewColumnConfig } from "../../../../models";
import {
  GovScoresPerfrormancesApiService,
  ItemApiService,
  KpisService,
  StructureViewService,
} from "../../../../services";

@Component({
  selector: "adaa-nested-table-row",
  imports: [TranslatePipe, PercentageValuePipe, FontAwesomeModule],
  templateUrl: "./nested-table-row.html",
  styleUrl: "./nested-table-row.scss",
})
export class NestedTableRowComponent implements OnInit, OnChanges {
  private _iconLibrary = inject(FaIconLibrary);
  private _itemApiService = inject(ItemApiService);
  private _govScoresPerfrormancesApiService = inject(GovScoresPerfrormancesApiService);
  private _kpiService = inject(KpisService);
  structureViewService = inject(StructureViewService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data = input.required<any>();
  columns = input.required<StructureViewColumnConfig[]>();
  isEven = input.required<boolean>();
  level = input.required<number>();
  periodId = input.required<number>();
  useGovScoresConfig = input<boolean>(false);

  hasChildKey = computed(() => (this.useGovScoresConfig() ? "hasChildren" : "hasChilds"));

  expanded = signal<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  childs = signal<any[]>([]);

  AdaaBoolean = AdaaBoolean;
  AdaaHelper = AdaaHelper;
  Constants = Constants;

  isArabic = computed(() => AdaaHelper.getCurrentLang() === Language.Arabic);

  public ngOnInit(): void {
    this._iconLibrary.addIcons(faAngleLeft, faAngleRight, faAngleDown);
  }

  public ngOnChanges({ data }: SimpleChanges): void {
    if (JSON.stringify(data?.previousValue ?? []) !== JSON.stringify(data?.currentValue ?? [])) {
      const index = this.structureViewService.openedNodes.findIndex((e) => e === this.data().id);
      if (index > -1) {
        this.expanded.set(true);
        this.useGovScoresConfig() ? this._getGovScoresChilds() : this._getChilds();
      }
    }
  }

  public expandCollapseChild(isexpanded: boolean) {
    this.expanded.set(isexpanded);
    if (isexpanded) {
      this.structureViewService.openedNodes.push(this.data().id);
      this.useGovScoresConfig() ? this._getGovScoresChilds() : this._getChilds();
    } else {
      this.childs.set([]);
      const index = this.structureViewService.openedNodes.findIndex((e) => e === this.data().id);
      if (index > -1) this.structureViewService.openedNodes.splice(index, 1);
    }
  }

  private _getChilds() {
    this._itemApiService
      .getAllChildItems<ItemModel>({
        itemId: this.data().id,
        currentType: this.data().type,
        periodId: this.periodId(),
        useV2: true,
      })
      .subscribe({
        next: (response) => {
          if (response.inError) return;
          this.childs.set(response.responseData ?? []);
        },
      });
  }

  private _getGovScoresChilds() {
    const itemTypeId = this.data().itemTypeId;

    if (itemTypeId === Constants.GOV_SCORES_SECTOR) this._getGovScores(Constants.GOV_SCORES_ENTITY);
    else if (itemTypeId === Constants.GOV_SCORES_ENTITY) this._getGovScores(Constants.GOV_SCORES_PILLAR);
    else if (itemTypeId === Constants.GOV_SCORES_PILLAR) this._getGovScores(Constants.GOV_SCORES_SCOPE);
    else if (itemTypeId === Constants.GOV_SCORES_SCOPE) this._getKpis();
    else if (this._kpiService.checkIfItemKpi(this.data().itemTypeId)) this._getKpiChilds();
  }

  private _getGovScores(itemType: number) {
    this._govScoresPerfrormancesApiService
      .getScoresByPeriodAndItemType(this.periodId(), itemType, this.data().id)
      .subscribe({
        next: (response) => {
          if (response.inError) return;

          this.childs.set(response.responseData ?? []);
        },
      });
  }

  private _getKpis() {
    this._govScoresPerfrormancesApiService.getKpisByPeriodAndScopeId(this.periodId(), this.data().id).subscribe({
      next: (response) => {
        if (response.inError) return;

        this.childs.set(response.responseData ?? []);
      },
    });
  }

  private _getKpiChilds() {
    this._govScoresPerfrormancesApiService.getKpiChildren(this.data().id).subscribe({
      next: (response) => {
        if (response.inError) return;

        this.childs.set(response.responseData ?? []);
      },
    });
  }
}
